import React, { useEffect, useMemo, useState } from "react";
import { ButtonStyled } from "@nike/nike-design-system-components";
import PgStatusChip from "./PgStatusChip";
import { formatCurrentlyAssignedPgsLabel } from "./pgSpurAssignmentUtils";

function PgReassignSpurModal({
  open,
  sourceSpur,
  palletGroupsToMove,
  eligibleTargetSpurs,
  assignmentsBySpur,
  onCancel,
  onConfirm,
}) {
  const [selectedTargetSpurId, setSelectedTargetSpurId] = useState(null);

  useEffect(() => {
    setSelectedTargetSpurId(null);
  }, [open, sourceSpur?.spur]);

  const pgCount = palletGroupsToMove.length;

  const title = useMemo(() => {
    if (!sourceSpur || pgCount === 0) {
      return "Reassign to another Spur";
    }
    if (pgCount === 1) {
      return `Reassign ${palletGroupsToMove[0].palletGroup} from ${sourceSpur.spur} to another Spur`;
    }
    return `Reassign ${pgCount} Pallet Groups from ${sourceSpur.spur} to another Spur`;
  }, [sourceSpur, pgCount, palletGroupsToMove]);

  const contextMessage = useMemo(() => {
    if (!sourceSpur || pgCount === 0) {
      return null;
    }
    const noun = pgCount === 1 ? "PG" : `${pgCount} PGs`;
    return { noun, sourceSpur };
  }, [sourceSpur, pgCount]);

  const confirmLabel =
    pgCount <= 1 ? "Reassign" : `Reassign ${pgCount} PGs`;

  if (!open || !sourceSpur) {
    return null;
  }

  return (
    <div
      className="pg-modal-backdrop pg-modal-backdrop--layer-reassign"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div
        className="pg-modal pg-modal--reassign"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pg-reassign-modal-title"
        style={{ "--pg-reassign-row-count": Math.max(eligibleTargetSpurs.length, 1) }}
      >
        <div className="pg-modal__header">
          <div className="pg-modal__title" id="pg-reassign-modal-title">
            {title}
          </div>
          <button
            type="button"
            className="pg-modal__close"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="pg-modal__body pg-modal__body--reassign">
          {contextMessage && (
            <div className="pg-reassign-context">
              Moving {contextMessage.noun} —{" "}
              {palletGroupsToMove.map((p, index) => (
                <React.Fragment key={p.id}>
                  {index > 0 ? ", " : ""}
                  <strong>{p.palletGroup}</strong>
                </React.Fragment>
              ))}{" "}
              — from spur <strong>{contextMessage.sourceSpur.spur}</strong> (
              {contextMessage.sourceSpur.spurStatus}).
            </div>
          )}
          <p className="pg-reassign-hint">
            Choose a target spur (Suspended spurs and the source spur are excluded):
          </p>
          <div
            className="pg-reassign-table-wrap"
            role="region"
            aria-label="Eligible target spurs"
            tabIndex={0}
          >
            <table className="pg-modal-table pg-modal-table--reassign">
              <thead>
                <tr>
                  <th className="pg-reassign-col-select" scope="col" aria-label="Select spur" />
                  <th scope="col">Spur ID</th>
                  <th scope="col">Status</th>
                  <th scope="col">Currently Assigned PGs</th>
                </tr>
              </thead>
              <tbody>
                {eligibleTargetSpurs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="pg-reassign-empty">
                      No eligible target spurs (all others are Suspended or don&apos;t
                      exist).
                    </td>
                  </tr>
                ) : (
                  eligibleTargetSpurs.map((spur) => {
                    const assignedLabel = formatCurrentlyAssignedPgsLabel(
                      spur,
                      assignmentsBySpur,
                    );
                    const isSelected = selectedTargetSpurId === spur.spur;
                    return (
                      <tr
                        key={spur.id}
                        className={isSelected ? "pg-reassign-row--selected" : ""}
                        onClick={() => setSelectedTargetSpurId(spur.spur)}
                      >
                        <td className="pg-reassign-col-select">
                          <input
                            type="radio"
                            name="pg-reassign-target"
                            checked={isSelected}
                            onChange={() => setSelectedTargetSpurId(spur.spur)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select spur ${spur.spur}`}
                          />
                        </td>
                        <td className="pg-cell-strong">{spur.spur}</td>
                        <td>
                          <PgStatusChip
                            status={spur.spurStatus}
                            readyToComplete={spur.readyToComplete}
                          />
                        </td>
                        <td>
                          {assignedLabel ? (
                            assignedLabel
                          ) : (
                            <span className="pg-cell-muted">Idle — no PGs</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="pg-modal__footer">
          <ButtonStyled
            appearance="primary"
            type="button"
            className="pg-btn-reset"
            onClick={onCancel}
          >
            Cancel
          </ButtonStyled>
          <ButtonStyled
            appearance="primary"
            type="button"
            className="pg-btn-apply"
            disabled={!selectedTargetSpurId || pgCount === 0}
            onClick={() => onConfirm(selectedTargetSpurId)}
          >
            {confirmLabel}
          </ButtonStyled>
        </div>
      </div>
    </div>
  );
}

export default PgReassignSpurModal;
